import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import PageTransition from '../../components/animations/PageTransition';
import { 
    ShieldCheck, 
    Search, 
    Award, 
    BookOpen, 
    Users, 
    TrendingUp, 
    Building, 
    ExternalLink, 
    ArrowRight, 
    Trophy, 
    CheckCircle2, 
    Sparkles, 
    Calendar,
    Mail,
    Globe,
    Bot
} from 'lucide-react';
import { VERIFIED_MEMBERS } from './VerifyCertificate';
import { getRankedMembers } from '../../services/referralService';

// Individual Faculty Photo / Avatar Component with cache buster & fallback initials
const FacultyAvatar = ({ name, refId, size = 64 }) => {
    const [imgError, setImgError] = useState(false);
    const cleanRef = refId ? refId.replace(/\//g, '_') : '';
    const photoPath = `/images/faculty/${cleanRef}.jpg?v=20261031_2`;
    
    const cleanParts = name.replace(/Dr\.|Mr\.|Mrs\.|Prof\.|Ms\.|Ph\.D|M\.Phil/gi, '').trim().split(' ').filter(Boolean);
    const initials = cleanParts.map(p => p[0]).slice(0, 2).join('').toUpperCase() || 'AN';

    if (!imgError && cleanRef) {
        return (
            <img 
                src={photoPath} 
                alt={name} 
                onError={() => setImgError(true)}
                style={{
                    width: `${size}px`,
                    height: `${size}px`,
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '2.5px solid #10b981',
                    boxShadow: '0 4px 12px rgba(4, 120, 87, 0.2)',
                    flexShrink: 0
                }}
            />
        );
    }

    return (
        <div style={{
            width: `${size}px`,
            height: `${size}px`,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #064e3b 0%, #047857 50%, #065f46 100%)',
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: '800',
            fontSize: `${size * 0.36}px`,
            border: '2.5px solid #34d399',
            boxShadow: '0 4px 10px rgba(6, 78, 59, 0.2)',
            flexShrink: 0,
            letterSpacing: '1px'
        }}>
            <span>{initials}</span>
        </div>
    );
};

const LandingPage = () => {
    const navigate = useNavigate();
    const { t } = useLanguage();
    const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth <= 768);
    const [verifyInput, setVerifyInput] = useState('');
    const [currentBanner, setCurrentBanner] = useState(0);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const heroBanners = [
        '/images/banners/BNAA.png',
        '/images/banners/JBB.png',
        '/images/banners/Gemini_Generated_Image_oh5887oh5887oh58.png',
        '/images/banners/Gemini_Generated_Image_t470wzt470wzt470.png',
        '/images/banners/Gemini_Generated_Image_vrhr8wvrhr8wvrhr.png',
    ];

    useEffect(() => {
        const bannerTimer = setInterval(() => {
            setCurrentBanner((prev) => (prev + 1) % heroBanners.length);
        }, 4000);
        return () => clearInterval(bannerTimer);
    }, [heroBanners.length]);

    const partners = [
        { name: 'Ministry of MSME, Govt. of India', logo: '/images/logos/msme-logo.png', height: '56px' },
        { name: 'Jjegan Graphers (Patron Entity)', logo: '/images/logos/jjegan-logo.png', height: '56px' },
        { name: 'Kani Rathnam Equality Foundation', logo: '/images/logos/kani-rathnam-logo.png', height: '56px' },
        { name: 'Assist World Records Research', logo: '/images/logos/assist-world-records.png', height: '56px' },
        { name: 'Prema AI Labs (Technology Partner)', logo: '/images/logos/prema-ai-labs.png', height: '56px', link: 'https://premaailabs.tech/' },
    ];

    // Compute live top leaderboard contenders
    const rankedMembers = getRankedMembers(VERIFIED_MEMBERS);
    const topContenders = rankedMembers.slice(0, 4);

    // Featured 6 scholars for home page spotlight
    const featuredScholars = [
        VERIFIED_MEMBERS.find(m => m.refId === 'ANRD/AB/2026/002'), // Dr. D. Lourdhu Mary
        VERIFIED_MEMBERS.find(m => m.refId === 'ANRD/AB/2026/022'), // Dr. B. Balamayurathan
        VERIFIED_MEMBERS.find(m => m.refId === 'ANRD/AM/2026/008'), // Dr. T. Ezhilarasi
        VERIFIED_MEMBERS.find(m => m.refId === 'ANRD/AB/2026/018'), // Dr. K. Vinoth
        VERIFIED_MEMBERS.find(m => m.refId === 'ANRD/AB/2026/001'), // Mrs. M. Geetha
        VERIFIED_MEMBERS.find(m => m.refId === 'ANRD/AM/2026/003'), // Mr. Brahma Naidu Chavali
    ].filter(Boolean);

    const handleQuickVerify = (e) => {
        e && e.preventDefault();
        const trimmed = verifyInput.trim();
        if (trimmed) {
            navigate(`/verify?id=${encodeURIComponent(trimmed)}`);
        } else {
            navigate('/verify');
        }
    };

    return (
        <PageTransition>
            <div style={{ background: '#f8fafc', color: '#0f172a', overflowX: 'hidden' }}>

                {/* =========================================================
                    1. 🏆 2026 Committee Leadership Election Notice Bar
                   ========================================================= */}
                <div style={{
                    background: 'linear-gradient(90deg, #064e3b 0%, #047857 50%, #065f46 100%)',
                    color: '#ffffff',
                    padding: '12px 16px',
                    boxShadow: '0 4px 12px rgba(6, 78, 59, 0.25)',
                    position: 'relative',
                    zIndex: 40
                }}>
                    <div className="container" style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: '12px',
                        maxWidth: '100%',
                        padding: '0 32px'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ fontSize: '18px' }}>🔥</span>
                            <div style={{ fontSize: isMobile ? '12.5px' : '14px', lineHeight: 1.4 }}>
                                <strong style={{ color: '#fef08a' }}>2026 Advisory Committee Elections:</strong>{' '}
                                Lifetime Institutional Membership at promotional fee of <strong>₹100</strong> until Nov 30!
                                {' '}<span style={{ opacity: 0.9 }}>Quote your Professor's Reference ID to vote for them!</span>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <button
                                onClick={() => navigate('/verify')}
                                style={{
                                    background: '#fef08a',
                                    color: '#0f172a',
                                    border: 'none',
                                    padding: '6px 14px',
                                    borderRadius: '6px',
                                    fontSize: '12.5px',
                                    fontWeight: '800',
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                }}
                            >
                                🏆 Live Points Race &rarr;
                            </button>
                            <a
                                href="https://forms.gle/UfkUncKELYenUJE46"
                                target="_blank"
                                rel="noreferrer"
                                style={{
                                    background: 'rgba(255,255,255,0.18)',
                                    color: '#ffffff',
                                    border: '1px solid rgba(255,255,255,0.3)',
                                    padding: '6px 14px',
                                    borderRadius: '6px',
                                    fontSize: '12.5px',
                                    fontWeight: '700',
                                    textDecoration: 'none',
                                    display: 'inline-flex',
                                    alignItems: 'center'
                                }}
                            >
                                ✍️ Register (₹100)
                            </a>
                        </div>
                    </div>
                </div>

                {/* =========================================================
                    2. 🏛️ INSTITUTIONAL HERO SECTION
                   ========================================================= */}
                <div style={styles.heroSection}>
                    <div style={styles.heroBackground}></div>

                    <div className="container" style={{
                        maxWidth: '1200px',
                        margin: '0 auto',
                        padding: isMobile ? '40px 16px 60px' : '70px 20px 80px',
                        position: 'relative',
                        zIndex: 10,
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: isMobile ? '36px' : '50px'
                    }}>
                        {/* Left: Text & Primary CTAs */}
                        <div style={{ flex: '1.2', textAlign: isMobile ? 'center' : 'left' }}>
                            {/* Govt. Accreditation Eyebrow */}
                            <div style={styles.promoTag}>
                                <ShieldCheck size={16} color="#34d399" />
                                <span>Ministry of MSME, Govt. of India &bull; UDYAM-TN-32-0048110</span>
                            </div>

                            <h1 style={styles.heroTitle}>
                                Multidisciplinary <br />
                                <span style={styles.gradientText}>Research & Development</span>
                            </h1>

                            <p style={styles.heroSubtitle}>
                                An apex institutional R&D and academic council patronized by <strong>Jjegan Graphers</strong> and partnering with <strong>Kani Rathnam Equality Foundation</strong>. Uniting <strong>{VERIFIED_MEMBERS.length}+ distinguished professors, scientists, and researchers</strong> across India and internationally under a 10-year council tenure (2026–2036).
                            </p>

                            {/* Action Buttons */}
                            <div style={{
                                display: 'flex',
                                gap: '14px',
                                flexWrap: 'wrap',
                                justifyContent: isMobile ? 'center' : 'flex-start',
                                marginTop: '28px'
                            }}>
                                <button
                                    onClick={() => navigate('/faculty')}
                                    style={styles.primaryBtn}
                                >
                                    <Users size={18} />
                                    <span>Advisory Council & Faculty &rarr;</span>
                                </button>
                                <button
                                    onClick={() => navigate('/verify')}
                                    style={styles.secondaryBtn}
                                >
                                    <ShieldCheck size={18} color="#34d399" />
                                    <span>Verify Credentials / Ref ID</span>
                                </button>
                                <a
                                    href="https://forms.gle/UfkUncKELYenUJE46"
                                    target="_blank"
                                    rel="noreferrer"
                                    style={styles.outlineBtn}
                                >
                                    <Sparkles size={16} color="#fef08a" />
                                    <span>Join Council (₹100) ↗</span>
                                </a>
                            </div>

                            {/* Trust badges row */}
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: isMobile ? '12px' : '20px',
                                flexWrap: 'wrap',
                                justifyContent: isMobile ? 'center' : 'flex-start',
                                marginTop: '36px',
                                paddingTop: '24px',
                                borderTop: '1px solid rgba(255,255,255,0.1)'
                            }}>
                                <div style={styles.miniTrustBadge}>
                                    <Trophy size={16} color="#fbbf24" />
                                    <span>{VERIFIED_MEMBERS.length}+ Inducted Scholars</span>
                                </div>
                                <div style={styles.miniTrustBadge}>
                                    <Calendar size={16} color="#60a5fa" />
                                    <span>10-Year Council (2026–2036)</span>
                                </div>
                                <div style={styles.miniTrustBadge}>
                                    <BookOpen size={16} color="#34d399" />
                                    <span>44+ ISBN / ISSN Works</span>
                                </div>
                            </div>
                        </div>

                        {/* Right: Rotating Institutional Banners */}
                        <div style={{ flex: '0.9', width: isMobile ? '100%' : 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <div style={isMobile ? { ...styles.heroBannerFrame, width: '100%', borderRadius: '18px' } : styles.heroBannerFrame}>
                                <img
                                    src={heroBanners[currentBanner]}
                                    alt={`Arivu Nest R&D Banner ${currentBanner + 1}`}
                                    style={isMobile ? { ...styles.heroBannerImage, height: '240px' } : styles.heroBannerImage}
                                />
                            </div>
                            {/* Dot Indicators */}
                            <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                                {heroBanners.map((_, idx) => (
                                    <button
                                        type="button"
                                        key={idx}
                                        onClick={() => setCurrentBanner(idx)}
                                        aria-label={`Go to slide ${idx + 1}`}
                                        style={{
                                            width: currentBanner === idx ? '24px' : '8px',
                                            height: '8px',
                                            borderRadius: '4px',
                                            border: 'none',
                                            background: currentBanner === idx ? '#10b981' : 'rgba(255,255,255,0.25)',
                                            cursor: 'pointer',
                                            transition: 'all 0.3s'
                                        }}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* =========================================================
                    3. 🔍 INSTANT VERIFICATION SEARCH BAR (Floating Card)
                   ========================================================= */}
                <div style={{
                    marginTop: '-40px',
                    position: 'relative',
                    zIndex: 30,
                    padding: '0 16px'
                }}>
                    <div style={{
                        maxWidth: '1000px',
                        margin: '0 auto',
                        background: '#ffffff',
                        borderRadius: '20px',
                        boxShadow: '0 20px 40px -10px rgba(15, 23, 42, 0.15)',
                        border: '1.5px solid #e2e8f0',
                        padding: isMobile ? '20px' : '26px 32px'
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: '12px',
                            marginBottom: '16px'
                        }}>
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <ShieldCheck size={20} color="#059669" />
                                    <h3 style={{ margin: 0, fontSize: isMobile ? '16px' : '18px', fontWeight: '800', color: '#0f172a' }}>
                                        Instant Council Member & Certificate Verification
                                    </h3>
                                </div>
                                <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>
                                    Verify authentic appointment credentials, tenure validity, and institutional affiliation in real time.
                                </p>
                            </div>
                            <span style={{
                                background: '#ecfdf5',
                                color: '#047857',
                                border: '1px solid #a7f3d0',
                                padding: '4px 10px',
                                borderRadius: '999px',
                                fontSize: '12px',
                                fontWeight: '700'
                            }}>
                                {VERIFIED_MEMBERS.length} Verified Scholars Live
                            </span>
                        </div>

                        <form onSubmit={handleQuickVerify} style={{
                            display: 'flex',
                            gap: '10px',
                            flexDirection: isMobile ? 'column' : 'row'
                        }}>
                            <div style={{
                                position: 'relative',
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center'
                            }}>
                                <Search size={18} color="#94a3b8" style={{ position: 'absolute', left: '16px' }} />
                                <input
                                    type="text"
                                    value={verifyInput}
                                    onChange={(e) => setVerifyInput(e.target.value)}
                                    placeholder="Enter Ref ID (e.g. ANRD/AB/2026/002) or Professor Name..."
                                    style={{
                                        width: '100%',
                                        padding: '14px 16px 14px 46px',
                                        borderRadius: '12px',
                                        border: '1.5px solid #cbd5e1',
                                        fontSize: '14.5px',
                                        outline: 'none',
                                        background: '#f8fafc',
                                        color: '#0f172a'
                                    }}
                                />
                            </div>
                            <button
                                type="submit"
                                style={{
                                    background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                                    color: '#ffffff',
                                    border: 'none',
                                    borderRadius: '12px',
                                    padding: isMobile ? '14px' : '14px 28px',
                                    fontSize: '14.5px',
                                    fontWeight: '800',
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px',
                                    boxShadow: '0 4px 14px rgba(5, 150, 105, 0.3)'
                                }}
                            >
                                <Search size={16} />
                                <span>Verify Credentials &rarr;</span>
                            </button>
                        </form>

                        {/* Quick sample chips */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            flexWrap: 'wrap',
                            marginTop: '14px',
                            fontSize: '12.5px',
                            color: '#64748b'
                        }}>
                            <span>Quick Lookups:</span>
                            {[
                                { label: 'Dr. D. Lourdhu Mary (002)', id: 'ANRD/AB/2026/002' },
                                { label: 'Dr. B. Balamayurathan (022)', id: 'ANRD/AB/2026/022' },
                                { label: 'Dr. K. Vinoth (018)', id: 'ANRD/AB/2026/018' },
                                { label: 'Dr. K. Muthulakshmi (045)', id: 'ANRD/AB/2026/045' },
                                { label: 'Mr. S. Nagarajan (046)', id: 'ANRD/AM/2026/046' },
                                { label: 'Ms. L. Kanimozhi (047)', id: 'ANRD/AM/2026/047' },
                                { label: 'Ms. G. Hemaladevi (048)', id: 'ANRD/AM/2026/048' },
                                { label: 'Dr. N. A. S. Vinoth (049)', id: 'ANRD/AM/2026/049' },
                                { label: 'Dr. Kalpana C (050)', id: 'ANRD/AB/2026/050' },
                                { label: 'Dr. S. Delsi Robinsha (051)', id: 'ANRD/AB/2026/051' },
                                { label: 'Ms. Tanusya (052)', id: 'ANRD/AM/2026/052' },
                                { label: 'Prof. B. Indrajith (053)', id: 'ANRD/AM/2026/053' },
                                { label: 'Prof. Julia P (054)', id: 'ANRD/AB/2026/054' },
                                { label: 'Dr. Aparna Ramesh (055)', id: 'ANRD/AM/2026/055' },
                                { label: 'Dr. A. Bala Vanitha (056)', id: 'ANRD/AB/2026/056' },
                                { label: 'Dr. M. Hamsalatha (057)', id: 'ANRD/AM/2026/057' },
                                { label: 'Dr. R. Bathridevanath (058)', id: 'ANRD/AB/2026/058' },
                                { label: 'A. Lincy Annet (059)', id: 'ANRD/AM/2026/059' },
                                { label: 'Dr. L. Sugirtha (060)', id: 'ANRD/AB/2026/060' },
                                { label: 'Dr. M. Vasanthamullai (061)', id: 'ANRD/AB/2026/061' },
                                { label: 'Prof. J. Farida (062)', id: 'ANRD/AM/2026/062' },
                            ].map((chip) => (
                                <button
                                    key={chip.id}
                                    type="button"
                                    onClick={() => navigate(`/verify?id=${encodeURIComponent(chip.id)}`)}
                                    style={{
                                        background: '#f1f5f9',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '6px',
                                        padding: '3px 9px',
                                        fontSize: '12px',
                                        color: '#0f172a',
                                        cursor: 'pointer',
                                        fontWeight: '600'
                                    }}
                                >
                                    {chip.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* =========================================================
                    4. 📊 INSTITUTIONAL METRICS BAR
                   ========================================================= */}
                <section style={{ padding: isMobile ? '50px 16px 40px' : '70px 20px 50px' }}>
                    <div className="container" style={{ maxWidth: '1200px', margin: '0 auto' }}>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                            gap: '20px'
                        }}>
                            {[
                                {
                                    val: `${VERIFIED_MEMBERS.length}+`,
                                    label: 'Inducted Council Scholars',
                                    sub: 'Advisory Board & Academic Council',
                                    icon: <Users size={28} color="#059669" />
                                },
                                {
                                    val: '2026–2036',
                                    label: '10-Year Council Tenure',
                                    sub: 'Strategic Institutional Framework',
                                    icon: <Calendar size={28} color="#2563eb" />
                                },
                                {
                                    val: '44+',
                                    label: 'ISBN Books & ISSN Papers',
                                    sub: 'Peer-Reviewed Multidisciplinary Research',
                                    icon: <BookOpen size={28} color="#7c3aed" />
                                },
                                {
                                    val: 'UDYAM-TN-32',
                                    label: 'Ministry of MSME, Govt. of India',
                                    sub: 'Reg No: UDYAM-TN-32-0048110',
                                    icon: <Building size={28} color="#d97706" />
                                },
                            ].map((stat, i) => (
                                <div key={i} style={{
                                    background: '#ffffff',
                                    border: '1.5px solid #e2e8f0',
                                    borderRadius: '16px',
                                    padding: '24px',
                                    boxShadow: '0 4px 12px rgba(15, 23, 42, 0.04)',
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: '16px'
                                }}>
                                    <div style={{
                                        width: '52px',
                                        height: '52px',
                                        borderRadius: '12px',
                                        background: '#f8fafc',
                                        border: '1px solid #e2e8f0',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0
                                    }}>
                                        {stat.icon}
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '24px', fontWeight: '900', color: '#0f172a', lineHeight: 1.1 }}>
                                            {stat.val}
                                        </div>
                                        <div style={{ fontSize: '14px', fontWeight: '700', color: '#334155', marginTop: '4px' }}>
                                            {stat.label}
                                        </div>
                                        <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                                            {stat.sub}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* =========================================================
                    5. 🏆 2026 COMMITTEE ELECTIONS & LIVE LEADERBOARD
                   ========================================================= */}
                <section style={{
                    padding: isMobile ? '40px 16px' : '60px 20px',
                    background: 'linear-gradient(180deg, #ecfdf5 0%, #f0fdf4 100%)',
                    borderTop: '1px solid #a7f3d0',
                    borderBottom: '1px solid #a7f3d0'
                }}>
                    <div className="container" style={{ maxWidth: '1200px', margin: '0 auto' }}>
                        <div style={{ textAlign: 'center', marginBottom: '36px' }}>
                            <div style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                background: '#d1fae5',
                                color: '#065f46',
                                border: '1px solid #6ee7b7',
                                padding: '5px 14px',
                                borderRadius: '999px',
                                fontSize: '11.5px',
                                fontWeight: '800',
                                letterSpacing: '0.5px',
                                textTransform: 'uppercase',
                                marginBottom: '10px'
                            }}>
                                <Trophy size={14} color="#059669" />
                                <span>Official 2026 Governance Race</span>
                            </div>
                            <h2 style={{ fontSize: 'clamp(22px, 4vw, 32px)', color: '#064e3b', fontWeight: '800', margin: '0 0 10px 0' }}>
                                2026 Executive Committee Leadership Race
                            </h2>
                            <p style={{ color: '#047857', fontSize: '15px', maxWidth: '750px', margin: '0 auto', lineHeight: '1.6' }}>
                                Council appointments to <strong>President</strong>, <strong>Vice President</strong>, <strong>Secretary</strong>, and <strong>Joint Secretary</strong> are awarded to members through verified institutional referrals. Every new registration citing a member awards <strong>+10 points</strong>.
                            </p>
                        </div>

                        {/* Top 4 Contenders Grid */}
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                            gap: '20px',
                            marginBottom: '30px'
                        }}>
                            {topContenders.map((member, index) => {
                                const rankBadge = [
                                    { title: '👑 President Contender', bg: '#fef3c7', text: '#92400e', border: '#fcd34d' },
                                    { title: '🥈 Vice President Contender', bg: '#f1f5f9', text: '#334155', border: '#cbd5e1' },
                                    { title: '🥉 Secretary Contender', bg: '#ffedd5', text: '#9a3412', border: '#fdba74' },
                                    { title: '🎖️ Joint Secretary Contender', bg: '#e0e7ff', text: '#3730a3', border: '#a5b4fc' },
                                ][index];

                                return (
                                    <div key={member.refId} style={{
                                        background: '#ffffff',
                                        border: `2px solid ${rankBadge.border}`,
                                        borderRadius: '16px',
                                        padding: '22px',
                                        boxShadow: '0 8px 20px -4px rgba(6, 78, 59, 0.08)',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-between',
                                        position: 'relative'
                                    }}>
                                        <div>
                                            <div style={{
                                                display: 'inline-block',
                                                background: rankBadge.bg,
                                                color: rankBadge.text,
                                                border: `1px solid ${rankBadge.border}`,
                                                padding: '4px 10px',
                                                borderRadius: '6px',
                                                fontSize: '11.5px',
                                                fontWeight: '800',
                                                marginBottom: '14px'
                                            }}>
                                                {rankBadge.title}
                                            </div>

                                            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '12px' }}>
                                                <FacultyAvatar name={member.name} refId={member.refId} size={54} />
                                                <div>
                                                    <h3 style={{ margin: 0, fontSize: '15.5px', fontWeight: '800', color: '#0f172a' }}>
                                                        {member.name}
                                                    </h3>
                                                    <span style={{ fontSize: '12px', color: '#059669', fontWeight: '700' }}>
                                                        {member.refId}
                                                    </span>
                                                </div>
                                            </div>

                                            <p style={{ fontSize: '12.5px', color: '#64748b', margin: '0 0 12px 0', lineHeight: 1.4 }}>
                                                {member.institution}
                                            </p>
                                        </div>

                                        <div style={{
                                            borderTop: '1px solid #f1f5f9',
                                            paddingTop: '12px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between'
                                        }}>
                                            <div>
                                                <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: '700' }}>
                                                    Verified Points
                                                </div>
                                                <div style={{ fontSize: '20px', fontWeight: '900', color: '#047857' }}>
                                                    {member.points} pts
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => navigate(`/verify?id=${encodeURIComponent(member.refId)}`)}
                                                style={{
                                                    background: '#ecfdf5',
                                                    color: '#065f46',
                                                    border: '1px solid #a7f3d0',
                                                    borderRadius: '6px',
                                                    padding: '6px 12px',
                                                    fontSize: '12px',
                                                    fontWeight: '700',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                Verify &rarr;
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Leaderboard CTA Footer */}
                        <div style={{
                            textAlign: 'center',
                            display: 'flex',
                            gap: '12px',
                            justifyContent: 'center',
                            flexWrap: 'wrap'
                        }}>
                            <button
                                onClick={() => navigate('/verify')}
                                style={{
                                    background: '#047857',
                                    color: '#ffffff',
                                    border: 'none',
                                    padding: '12px 24px',
                                    borderRadius: '10px',
                                    fontSize: '14px',
                                    fontWeight: '800',
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    boxShadow: '0 4px 12px rgba(4, 120, 87, 0.25)'
                                }}
                            >
                                <Trophy size={16} />
                                <span>View Full {VERIFIED_MEMBERS.length}-Member Points Leaderboard &rarr;</span>
                            </button>
                            <a
                                href="https://forms.gle/UfkUncKELYenUJE46"
                                target="_blank"
                                rel="noreferrer"
                                style={{
                                    background: '#ffffff',
                                    color: '#047857',
                                    border: '1.5px solid #047857',
                                    padding: '12px 24px',
                                    borderRadius: '10px',
                                    fontSize: '14px',
                                    fontWeight: '800',
                                    textDecoration: 'none',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}
                            >
                                <span>Nominate / Quote Ref ID in Form (₹100) ↗</span>
                            </a>
                        </div>
                    </div>
                </section>

                {/* =========================================================
                    6. 👥 DISTINGUISHED ADVISORY COUNCIL & FACULTY SHOWCASE
                   ========================================================= */}
                <section style={{ padding: isMobile ? '50px 16px' : '80px 20px', background: '#ffffff' }}>
                    <div className="container" style={{ maxWidth: '1200px', margin: '0 auto' }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'flex-end',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: '16px',
                            marginBottom: '40px'
                        }}>
                            <div>
                                <div style={{
                                    display: 'inline-block',
                                    background: '#eff6ff',
                                    color: '#1d4ed8',
                                    border: '1px solid #bfdbfe',
                                    padding: '4px 12px',
                                    borderRadius: '999px',
                                    fontSize: '11px',
                                    fontWeight: '800',
                                    letterSpacing: '1px',
                                    textTransform: 'uppercase',
                                    marginBottom: '8px'
                                }}>
                                    National Academic Directory
                                </div>
                                <h2 style={{ fontSize: 'clamp(24px, 4vw, 34px)', color: '#0f172a', fontWeight: '800', margin: 0 }}>
                                    Distinguished Advisory Council & Faculty
                                </h2>
                                <p style={{ color: '#64748b', fontSize: '15px', margin: '8px 0 0', maxWidth: '650px' }}>
                                    Eminent research supervisors, department chairs, and scholars driving multidisciplinary research and scholastic ethics.
                                </p>
                            </div>
                            <button
                                onClick={() => navigate('/faculty')}
                                style={{
                                    background: 'transparent',
                                    color: '#059669',
                                    border: '1.5px solid #059669',
                                    borderRadius: '8px',
                                    padding: '10px 18px',
                                    fontSize: '13.5px',
                                    fontWeight: '700',
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}
                            >
                                <span>View All {VERIFIED_MEMBERS.length} Scholars in Directory</span>
                                <ArrowRight size={15} />
                            </button>
                        </div>

                        {/* Faculty Spotlight Grid */}
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                            gap: '24px'
                        }}>
                            {featuredScholars.map((faculty) => (
                                <div
                                    key={faculty.refId}
                                    style={{
                                        background: '#f8fafc',
                                        border: '1.5px solid #e2e8f0',
                                        borderRadius: '16px',
                                        padding: '22px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-between',
                                        transition: 'transform 0.2s, box-shadow 0.2s'
                                    }}
                                >
                                    <div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '14px' }}>
                                            <FacultyAvatar name={faculty.name} refId={faculty.refId} size={64} />
                                            <div>
                                                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '800', color: '#0f172a' }}>
                                                    {faculty.name}
                                                </h3>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '3px' }}>
                                                    <span style={{
                                                        background: faculty.council === 'Advisory Board' ? '#eff6ff' : '#ecfdf5',
                                                        color: faculty.council === 'Advisory Board' ? '#1e40af' : '#065f46',
                                                        fontSize: '11px',
                                                        fontWeight: '700',
                                                        padding: '2px 8px',
                                                        borderRadius: '4px'
                                                    }}>
                                                        {faculty.council}
                                                    </span>
                                                    <span style={{ fontSize: '11.5px', color: '#059669', fontWeight: '700' }}>
                                                        {faculty.refId}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <p style={{ fontSize: '13px', fontWeight: '600', color: '#334155', margin: '0 0 6px 0' }}>
                                            {faculty.designation}
                                        </p>
                                        <p style={{ fontSize: '12.5px', color: '#64748b', margin: '0 0 16px 0', lineHeight: 1.4 }}>
                                            📍 {faculty.institution}, {faculty.location}
                                        </p>
                                    </div>

                                    <div style={{
                                        borderTop: '1px solid #e2e8f0',
                                        paddingTop: '12px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between'
                                    }}>
                                        <span style={{ fontSize: '11.5px', color: '#059669', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <CheckCircle2 size={14} /> ACTIVE & VERIFIED
                                        </span>
                                        <button
                                            onClick={() => navigate(`/verify?id=${encodeURIComponent(faculty.refId)}`)}
                                            style={{
                                                background: 'transparent',
                                                border: 'none',
                                                color: '#2563eb',
                                                fontWeight: '700',
                                                fontSize: '12.5px',
                                                cursor: 'pointer',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '4px'
                                            }}
                                        >
                                            View Credentials &rarr;
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* =========================================================
                    7. 🔬 4 STRATEGIC WINGS OF ARIVU NEST R&D
                   ========================================================= */}
                <section style={{ padding: isMobile ? '40px 16px' : '70px 20px', background: '#f8fafc', borderTop: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0' }}>
                    <div className="container" style={{ maxWidth: '1200px', margin: '0 auto' }}>
                        <div style={{ textAlign: 'center', marginBottom: '45px' }}>
                            <div style={{ display: 'inline-block', background: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0', padding: '5px 14px', borderRadius: '999px', fontSize: '11px', fontWeight: '800', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '12px' }}>
                                Institutional Architecture &bull; MSME Reg: UDYAM-TN-32-0048110
                            </div>
                            <h2 style={{ fontSize: 'clamp(24px, 4vw, 34px)', color: '#0f172a', fontWeight: '800', margin: '0 0 10px 0' }}>
                                The 4 Strategic Operational Wings of Arivu Nest R&D
                            </h2>
                            <p style={{ color: '#64748b', fontSize: '15px', maxWidth: '750px', margin: '0 auto' }}>
                                Patronized by <strong>Jjegan Graphers</strong> (Virudhunagar & Kovilpatti) and collaborating with <strong>Kani Rathnam Equality Foundation</strong> to integrate frontier AI, empirical research, and student incubation.
                            </p>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '24px' }}>
                            {/* Wing 1 */}
                            <div style={{ background: '#ffffff', border: '1.5px solid #e2e8f0', borderRadius: '14px', padding: '26px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                <div>
                                    <div style={{ fontSize: '32px', marginBottom: '12px' }}>🔬</div>
                                    <h3 style={{ margin: '0 0 8px 0', fontSize: '17px', fontWeight: '700', color: '#0f172a' }}>1. Research & Publications</h3>
                                    <p style={{ margin: '0 0 16px 0', fontSize: '13.5px', color: '#64748b', lineHeight: '1.6' }}>
                                        28 ISBN articles, 16 ISSN journal papers, and 3 edited academic books led by Dr. Sabitha in partnership with scholars across Singapore and Indonesia.
                                    </p>
                                </div>
                                <button onClick={() => navigate('/research')} style={{ background: 'transparent', border: 'none', color: '#059669', fontWeight: '700', fontSize: '13.5px', cursor: 'pointer', textAlign: 'left', padding: 0 }}>
                                    Explore Research &rarr;
                                </button>
                            </div>

                            {/* Wing 2 */}
                            <div style={{ background: '#ffffff', border: '1.5px solid #e2e8f0', borderRadius: '14px', padding: '26px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                <div>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                                        <div style={{ fontSize: '32px' }}>💼</div>
                                        <span style={{ background: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0', fontSize: '11px', fontWeight: '800', padding: '2px 8px', borderRadius: '6px', textTransform: 'uppercase' }}>
                                            2026 Batch Open
                                        </span>
                                    </div>
                                    <h3 style={{ margin: '0 0 8px 0', fontSize: '17px', fontWeight: '700', color: '#0f172a' }}>2. Internships & Mentorship</h3>
                                    <p style={{ margin: '0 0 16px 0', fontSize: '13.5px', color: '#64748b', lineHeight: '1.6' }}>
                                        Hands-on live engineering and linguistic projects for college students with MSME-accredited completion certificates and commendation honors.
                                    </p>
                                </div>
                                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                                    <button onClick={() => navigate('/internships')} style={{ background: '#059669', color: '#ffffff', border: 'none', borderRadius: '6px', padding: '6px 12px', fontWeight: '700', fontSize: '12.5px', cursor: 'pointer' }}>
                                        Apply Online (Google Form) &rarr;
                                    </button>
                                    <button onClick={() => navigate('/internships')} style={{ background: 'transparent', border: 'none', color: '#059669', fontWeight: '700', fontSize: '12.5px', cursor: 'pointer', padding: 0 }}>
                                        View Tracks
                                    </button>
                                </div>
                            </div>

                            {/* Wing 3 */}
                            <div style={{ background: '#ffffff', border: '1.5px solid #e2e8f0', borderRadius: '14px', padding: '26px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                <div>
                                    <div style={{ fontSize: '32px', marginBottom: '12px' }}>🗣️</div>
                                    <h3 style={{ margin: '0 0 8px 0', fontSize: '17px', fontWeight: '700', color: '#0f172a' }}>3. AI Communication Labs</h3>
                                    <p style={{ margin: '0 0 16px 0', fontSize: '13.5px', color: '#64748b', lineHeight: '1.6' }}>
                                        Interactive pronunciation analysis, Grammar Kata drills, and structured communication training engineered with Google Gemini.
                                    </p>
                                </div>
                                <button onClick={() => navigate('/courses')} style={{ background: 'transparent', border: 'none', color: '#059669', fontWeight: '700', fontSize: '13.5px', cursor: 'pointer', textAlign: 'left', padding: 0 }}>
                                    Enter Learning Labs &rarr;
                                </button>
                            </div>

                            {/* Wing 4 */}
                            <div style={{ background: '#ffffff', border: '1.5px solid #e2e8f0', borderRadius: '14px', padding: '26px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                <div>
                                    <div style={{ fontSize: '32px', marginBottom: '12px' }}>🕊️</div>
                                    <h3 style={{ margin: '0 0 8px 0', fontSize: '17px', fontWeight: '700', color: '#0f172a' }}>4. Community & Equality</h3>
                                    <p style={{ margin: '0 0 16px 0', fontSize: '13.5px', color: '#64748b', lineHeight: '1.6' }}>
                                        Grassroots educational outreach and collegiate workshops under Kani Rathnam Equality Foundation to ensure equal access to high-tier fluency.
                                    </p>
                                </div>
                                <button onClick={() => navigate('/about')} style={{ background: 'transparent', border: 'none', color: '#059669', fontWeight: '700', fontSize: '13.5px', cursor: 'pointer', textAlign: 'left', padding: 0 }}>
                                    Learn About Mission &rarr;
                                </button>
                            </div>
                        </div>
                    </div>
                </section>

                {/* =========================================================
                    8. 👑 EXECUTIVE GOVERNANCE & DIRECTORATE
                   ========================================================= */}
                <section style={{ padding: isMobile ? '50px 16px' : '80px 20px', background: '#ffffff' }}>
                    <div className="container" style={{ maxWidth: '1200px', margin: '0 auto' }}>
                        <div style={{ textAlign: 'center', marginBottom: '45px' }}>
                            <div style={{
                                display: 'inline-block',
                                background: '#fef3c7',
                                color: '#92400e',
                                border: '1px solid #fde68a',
                                padding: '5px 14px',
                                borderRadius: '999px',
                                fontSize: '11px',
                                fontWeight: '800',
                                letterSpacing: '1px',
                                textTransform: 'uppercase',
                                marginBottom: '12px'
                            }}>
                                Institutional Governance
                            </div>
                            <h2 style={{ fontSize: 'clamp(24px, 4vw, 34px)', color: '#0f172a', fontWeight: '800', margin: '0 0 10px 0' }}>
                                Executive Leadership & Directorate
                            </h2>
                            <p style={{ color: '#64748b', fontSize: '15px', maxWidth: '750px', margin: '0 auto' }}>
                                Stewarding institutional ethics, frontier technology infrastructure, and academic council governance across Arivu Nest R&D and Jjegan Graphers.
                            </p>
                        </div>

                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                            gap: '24px'
                        }}>
                            {/* Dr. S. Sabitha Shunmugapriya */}
                            <div style={{
                                background: '#f8fafc',
                                border: '1.5px solid #e2e8f0',
                                borderRadius: '18px',
                                padding: '28px',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                position: 'relative'
                            }}>
                                <div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
                                        <img
                                            src="/images/logos/dr-sabitha-portrait.jpg"
                                            alt="Dr. S. Sabitha Shunmugapriya"
                                            style={{
                                                width: '70px',
                                                height: '70px',
                                                borderRadius: '50%',
                                                objectFit: 'cover',
                                                border: '3px solid #059669',
                                                boxShadow: '0 4px 12px rgba(5, 150, 105, 0.2)'
                                            }}
                                        />
                                        <div>
                                            <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '800', color: '#0f172a' }}>
                                                Dr. S. Sabitha Shunmugapriya
                                            </h3>
                                            <div style={{ fontSize: '12.5px', color: '#059669', fontWeight: '700', marginTop: '2px' }}>
                                                Chairman & Founder
                                            </div>
                                            <div style={{ fontSize: '11.5px', color: '#64748b' }}>
                                                Arivu Nest R&D &bull; Jjegan Graphers
                                            </div>
                                        </div>
                                    </div>
                                    <p style={{ fontSize: '12.5px', color: '#047857', fontWeight: '700', margin: '0 0 8px 0' }}>
                                        M.A., M.Phil., B.Ed., TESEL, TEFL, PGCTE, PGDTE, Ph.D.
                                    </p>
                                    <p style={{ fontSize: '13px', color: '#475569', lineHeight: 1.5, margin: 0 }}>
                                        Assist World Record Holder, Author of 40+ ISBN/ISSN scholarly works, and pioneer of multidisciplinary language and research initiatives.
                                    </p>
                                </div>
                                <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '14px', marginTop: '16px', fontSize: '12.5px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <Mail size={14} color="#059669" />
                                    <span>dr.sabitha@arivunest.com</span>
                                </div>
                            </div>

                            {/* Nandhakumar Murugan */}
                            <div style={{
                                background: '#f8fafc',
                                border: '1.5px solid #e2e8f0',
                                borderRadius: '18px',
                                padding: '28px',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                position: 'relative'
                            }}>
                                <div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
                                        <div style={{
                                            width: '70px',
                                            height: '70px',
                                            borderRadius: '50%',
                                            background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                                            color: '#ffffff',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontWeight: '800',
                                            fontSize: '24px',
                                            border: '3px solid #60a5fa',
                                            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)'
                                        }}>
                                            NM
                                        </div>
                                        <div>
                                            <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '800', color: '#0f172a' }}>
                                                Nandhakumar Murugan
                                            </h3>
                                            <div style={{ fontSize: '12.5px', color: '#2563eb', fontWeight: '700', marginTop: '2px' }}>
                                                Chief Operations Officer (COO)
                                            </div>
                                            <div style={{ fontSize: '11.5px', color: '#64748b' }}>
                                                Head of Technology & Strategy
                                            </div>
                                        </div>
                                    </div>
                                    <p style={{ fontSize: '12.5px', color: '#1e40af', fontWeight: '700', margin: '0 0 8px 0' }}>
                                        Executive Secretariat &bull; Strategic Platforms
                                    </p>
                                    <p style={{ fontSize: '13px', color: '#475569', lineHeight: 1.5, margin: 0 }}>
                                        Leading operational scaling, verification architecture, institutional systems, and strategic technology integrations for Arivu Nest.
                                    </p>
                                </div>
                                <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '14px', marginTop: '16px', fontSize: '12.5px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <Mail size={14} color="#2563eb" />
                                    <span>nandhakumar@arivunest.com</span>
                                </div>
                            </div>

                            {/* Mohamed Irfan */}
                            <div style={{
                                background: '#f8fafc',
                                border: '1.5px solid #e2e8f0',
                                borderRadius: '18px',
                                padding: '28px',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                position: 'relative'
                            }}>
                                <div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
                                        <div style={{
                                            width: '70px',
                                            height: '70px',
                                            borderRadius: '50%',
                                            background: 'linear-gradient(135deg, #0f172a 0%, #334155 100%)',
                                            color: '#ffffff',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontWeight: '800',
                                            fontSize: '24px',
                                            border: '3px solid #94a3b8',
                                            boxShadow: '0 4px 12px rgba(15, 23, 42, 0.2)'
                                        }}>
                                            MI
                                        </div>
                                        <div>
                                            <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '800', color: '#0f172a' }}>
                                                Mohamed Irfan
                                            </h3>
                                            <div style={{ fontSize: '12.5px', color: '#0f172a', fontWeight: '700', marginTop: '2px' }}>
                                                Chief Technology Officer (CTO)
                                            </div>
                                            <div style={{ fontSize: '11.5px', color: '#64748b' }}>
                                                Head of Operations & Administration
                                            </div>
                                        </div>
                                    </div>
                                    <p style={{ fontSize: '12.5px', color: '#334155', fontWeight: '700', margin: '0 0 8px 0' }}>
                                        Technical Systems & Operational Governance
                                    </p>
                                    <p style={{ fontSize: '13px', color: '#475569', lineHeight: 1.5, margin: 0 }}>
                                        Directing technical infrastructure, system reliability, operational administration, and institutional deployment security.
                                    </p>
                                </div>
                                <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '14px', marginTop: '16px', fontSize: '12.5px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <Mail size={14} color="#0f172a" />
                                    <span>mooran@arivunest.com</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* =========================================================
                    9. 📖 DR. SABITHA'S CORNER: ENGLISH TRANSMISSION CLASSES
                   ========================================================= */}
                <section className="container" style={{ padding: isMobile ? '28px 16px 40px' : '56px 20px 80px', maxWidth: '1200px', margin: '0 auto' }}>
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-80px' }}
                        transition={{ duration: 0.5 }}
                        style={isMobile ? { ...styles.posterSection, gridTemplateColumns: '1fr' } : styles.posterSection}
                    >
                        <div style={styles.posterImageWrap}>
                            <img
                                src="/images/dr-sabitha-corner.jpg"
                                alt="Dr. Sabitha's Corner - English Transmission Classes"
                                style={styles.posterImage}
                            />
                        </div>

                        <div style={styles.posterContent}>
                            <div style={styles.posterEyebrow}>Communication Labs &bull; Dr. Sabitha's Corner</div>
                            <h2 style={styles.posterTitle}>English Transmission Classes</h2>
                            <p style={styles.posterDescription}>
                                A focused spoken English and language skills program covering reading, speaking,
                                writing, listening, and grammar support for learners and collegiate scholars at every stage.
                            </p>

                            <div style={styles.posterTopics}>
                                {[
                                    'Spoken English',
                                    'Reading skills',
                                    'Speaking skills',
                                    'Writing skills',
                                    'Listening skills',
                                    'Only Grammar',
                                ].map((item) => (
                                    <div key={item} style={styles.posterTopicItem}>
                                        <span style={styles.posterBullet}>›</span>
                                        <span>{item}</span>
                                    </div>
                                ))}
                            </div>

                            <div style={{ marginTop: '16px' }}>
                                <button
                                    onClick={() => navigate('/courses')}
                                    style={{
                                        background: '#1e3a8a',
                                        color: '#ffffff',
                                        border: 'none',
                                        padding: '12px 24px',
                                        borderRadius: '10px',
                                        fontSize: '14px',
                                        fontWeight: '700',
                                        cursor: 'pointer',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '6px'
                                    }}
                                >
                                    <span>Explore Language Curriculum</span>
                                    <ArrowRight size={15} />
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </section>

                {/* =========================================================
                    10. 🏛️ ACCREDITATIONS & INSTITUTIONAL PARTNERS
                   ========================================================= */}
                {partners.length > 0 && (
                    <section style={styles.accreditationsSection}>
                        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
                            <h2 style={styles.sectionTitle}>Accreditations & Institutional Patronage</h2>
                            <p style={{ textAlign: 'center', color: '#64748b', margin: '-16px auto 36px', maxWidth: '600px', fontSize: '14.5px' }}>
                                Recognized by government bodies and academic foundations committed to educational equity and research excellence.
                            </p>
                            <div style={styles.partnersGrid}>
                                {partners.map((partner, i) => {
                                    const cardEl = (
                                        <motion.div
                                            key={i}
                                            initial={{ opacity: 0, scale: 0.8 }}
                                            whileInView={{ opacity: 1, scale: 1 }}
                                            viewport={{ once: true }}
                                            transition={{ delay: i * 0.1 }}
                                            whileHover={{ scale: 1.05, boxShadow: '0 10px 24px -4px rgba(15, 23, 42, 0.1)' }}
                                            style={{
                                                ...styles.partnerCard,
                                                ...(partner.link ? { cursor: 'pointer', border: '1.5px solid #cbd5e1' } : {})
                                            }}
                                        >
                                            <div style={{ height: '70px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '12px' }}>
                                                <img src={partner.logo} alt={partner.name} style={{ maxHeight: partner.height, width: 'auto', objectFit: 'contain' }} />
                                            </div>
                                            <div style={{ 
                                                fontSize: '13.5px', 
                                                fontWeight: '700', 
                                                color: partner.link ? '#1e3a8a' : '#334155',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '5px'
                                            }}>
                                                <span>{partner.name}</span>
                                                {partner.link && <ExternalLink size={13} color="#2563eb" />}
                                            </div>
                                        </motion.div>
                                    );

                                    if (partner.link) {
                                        return (
                                            <a
                                                key={i}
                                                href={partner.link}
                                                target="_blank"
                                                rel="noreferrer"
                                                title={`Visit official site: ${partner.name}`}
                                                style={{ textDecoration: 'none', color: 'inherit', display: 'inline-block' }}
                                            >
                                                {cardEl}
                                            </a>
                                        );
                                    }
                                    return cardEl;
                                })}
                            </div>
                        </div>
                    </section>
                )}

                {/* =========================================================
                    11. 🚀 FINAL CALL TO ACTION: JOIN ADVISORY COUNCIL
                   ========================================================= */}
                <section style={{
                    padding: isMobile ? '50px 16px' : '80px 20px',
                    background: 'linear-gradient(135deg, #064e3b 0%, #047857 50%, #065f46 100%)',
                    color: '#ffffff',
                    textAlign: 'center'
                }}>
                    <div className="container" style={{ maxWidth: '900px', margin: '0 auto' }}>
                        <div style={{
                            display: 'inline-block',
                            background: 'rgba(255,255,255,0.15)',
                            padding: '6px 16px',
                            borderRadius: '999px',
                            fontSize: '12px',
                            fontWeight: '800',
                            letterSpacing: '1px',
                            textTransform: 'uppercase',
                            marginBottom: '16px'
                        }}>
                            Tenure 2026 – 2036 &bull; Lifetime Institutional Honor
                        </div>
                        <h2 style={{ fontSize: 'clamp(26px, 5vw, 42px)', fontWeight: '900', margin: '0 0 16px', lineHeight: 1.2 }}>
                            Join the Arivu Nest Academic Council & Advisory Board
                        </h2>
                        <p style={{ fontSize: '16px', opacity: 0.9, maxWidth: '680px', margin: '0 auto 32px', lineHeight: 1.6 }}>
                            Empower scholastic research, mentor collegiate scholars, and participate in institutional governance. Enroll during the promotional induction window at a nominal fee of <strong>₹100</strong> until November 30, 2026.
                        </p>
                        <div style={{ display: 'flex', gap: '14px', justifyContent: 'center', flexWrap: 'wrap' }}>
                            <a
                                href="https://forms.gle/UfkUncKELYenUJE46"
                                target="_blank"
                                rel="noreferrer"
                                style={{
                                    background: '#fef08a',
                                    color: '#0f172a',
                                    padding: '14px 30px',
                                    borderRadius: '12px',
                                    fontSize: '15px',
                                    fontWeight: '800',
                                    textDecoration: 'none',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    boxShadow: '0 8px 20px rgba(0,0,0,0.25)'
                                }}
                            >
                                <span>Apply via Official Google Form (₹100) ↗</span>
                            </a>
                            <button
                                onClick={() => navigate('/verify')}
                                style={{
                                    background: 'rgba(255,255,255,0.18)',
                                    color: '#ffffff',
                                    border: '1.5px solid rgba(255,255,255,0.35)',
                                    padding: '14px 26px',
                                    borderRadius: '12px',
                                    fontSize: '15px',
                                    fontWeight: '700',
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}
                            >
                                <ShieldCheck size={18} />
                                <span>Search Verification Directory</span>
                            </button>
                        </div>
                    </div>
                </section>

            </div>
        </PageTransition>
    );
};

const styles = {
    heroSection: {
        position: 'relative',
        background: '#0a0f1d',
        overflow: 'hidden',
        color: 'white',
        borderBottom: '1px solid rgba(255,255,255,0.08)'
    },
    heroBackground: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'radial-gradient(circle at 30% 20%, rgba(5, 150, 105, 0.22) 0%, rgba(10, 15, 29, 0.95) 75%)',
        zIndex: 1,
    },
    promoTag: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 16px',
        background: 'rgba(255, 255, 255, 0.08)',
        backdropFilter: 'blur(16px)',
        borderRadius: '50px',
        fontSize: '13px',
        fontWeight: '600',
        color: '#e2e8f0',
        marginBottom: '20px',
        border: '1px solid rgba(255, 255, 255, 0.16)',
    },
    heroTitle: {
        fontSize: 'clamp(32px, 5vw, 56px)',
        fontWeight: '900',
        lineHeight: '1.15',
        marginBottom: '18px',
        letterSpacing: '-0.02em',
        color: '#ffffff'
    },
    gradientText: {
        background: 'linear-gradient(135deg, #34d399 0%, #60a5fa 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
    },
    heroSubtitle: {
        fontSize: '15.5px',
        color: '#94a3b8',
        maxWidth: '580px',
        lineHeight: '1.65',
        margin: '0 0 24px 0',
    },
    primaryBtn: {
        padding: '13px 24px',
        background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '10px',
        fontSize: '14.5px',
        fontWeight: '800',
        cursor: 'pointer',
        boxShadow: '0 8px 20px -4px rgba(5, 150, 105, 0.4)',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
    },
    secondaryBtn: {
        padding: '13px 22px',
        background: 'rgba(255, 255, 255, 0.08)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        color: 'white',
        borderRadius: '10px',
        fontSize: '14.5px',
        fontWeight: '700',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
    },
    outlineBtn: {
        padding: '13px 20px',
        background: 'rgba(254, 240, 138, 0.12)',
        border: '1px solid rgba(254, 240, 138, 0.3)',
        color: '#fef08a',
        borderRadius: '10px',
        fontSize: '14px',
        fontWeight: '700',
        textDecoration: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
    },
    miniTrustBadge: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '13px',
        color: '#cbd5e1',
        fontWeight: '600'
    },
    heroBannerFrame: {
        width: 'min(500px, 100%)',
        borderRadius: '22px',
        overflow: 'hidden',
        border: '1.5px solid rgba(255, 255, 255, 0.18)',
        boxShadow: '0 20px 50px rgba(0, 0, 0, 0.5)',
        background: 'rgba(15, 23, 42, 0.6)',
    },
    heroBannerImage: {
        width: '100%',
        height: '340px',
        display: 'block',
        objectFit: 'cover',
    },
    posterSection: {
        display: 'grid',
        gridTemplateColumns: 'minmax(280px, 380px) minmax(280px, 1fr)',
        gap: '32px',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #fffdf4 0%, #f8f4e3 100%)',
        border: '1px solid #eadfb8',
        borderRadius: '24px',
        padding: '24px',
        boxShadow: '0 16px 40px rgba(15, 23, 42, 0.06)',
    },
    posterImageWrap: {
        borderRadius: '18px',
        overflow: 'hidden',
        background: '#fff',
        border: '1px solid rgba(15, 23, 42, 0.08)',
    },
    posterImage: {
        width: '100%',
        display: 'block',
        objectFit: 'cover',
    },
    posterContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
    },
    posterEyebrow: {
        display: 'inline-flex',
        alignSelf: 'flex-start',
        padding: '6px 12px',
        borderRadius: '999px',
        background: '#eef6ff',
        color: '#1e3a8a',
        fontSize: '12.5px',
        fontWeight: '700',
        letterSpacing: '0.02em',
    },
    posterTitle: {
        margin: 0,
        fontSize: 'clamp(24px, 3.5vw, 36px)',
        lineHeight: '1.2',
        color: '#1e3a8a',
        fontWeight: '800',
    },
    posterDescription: {
        margin: 0,
        color: '#475569',
        fontSize: '15px',
        lineHeight: '1.65',
        maxWidth: '56ch',
    },
    posterTopics: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '8px 16px',
        marginTop: '6px',
    },
    posterTopicItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        color: '#1e3a8a',
        fontSize: '15.5px',
        fontWeight: '700',
    },
    posterBullet: {
        color: '#c7d02c',
        fontSize: '24px',
        lineHeight: 1,
        fontWeight: '900',
    },
    accreditationsSection: {
        padding: '70px 0',
        background: 'white',
        borderTop: '1px solid #f1f5f9',
        borderBottom: '1px solid #f1f5f9',
    },
    sectionTitle: {
        textAlign: 'center',
        fontSize: 'clamp(24px, 4vw, 34px)',
        fontWeight: '800',
        color: '#0f172a',
        marginBottom: '24px',
        letterSpacing: '-0.02em',
    },
    partnersGrid: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: '30px',
        alignItems: 'center',
    },
    partnerCard: {
        background: '#f8fafc',
        padding: '24px 30px',
        borderRadius: '16px',
        textAlign: 'center',
        border: '1px solid #e2e8f0',
        boxShadow: '0 6px 16px -4px rgba(0,0,0,0.04)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '200px',
        transition: 'transform 0.2s',
        cursor: 'default',
    },
};

export default LandingPage;
